import { useState, useRef, useCallback, useEffect } from 'react';
import {
  ZoomIn, ZoomOut, RotateCcw, Maximize2, Minimize2,
  Sun, Contrast, SunMoon, X, PenLine
} from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export const CATEGORY_COLORS = {
  BONE: '#FACC15',
  HEMORRHAGE: '#EF4444',
  PNEUMOTHORAX: '#EF4444',
  NEURO: '#FB923C',
  EFFUSION: '#38BDF8',
  OTHER: '#22D3EE',
};

// Windowing-style presets emulated via CSS brightness/contrast
export const WINDOW_PRESETS = [
  { id: 'default', brightness: 100, contrast: 100 },
  { id: 'bone', brightness: 105, contrast: 185 },
  { id: 'lung', brightness: 135, contrast: 150 },
  { id: 'brain', brightness: 100, contrast: 210 },
  { id: 'soft', brightness: 120, contrast: 125 },
];

const ToolButton = ({ icon: Icon, label, onClick, active, testId }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClick}
          className={`h-8 w-8 p-0 ${active ? 'bg-cyan-500/20 text-cyan-300' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid={testId}
        >
          <Icon size={15} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const ImageViewer = ({
  images = [],
  priorImages = [],
  markers = [],
  annotations = [],
  annotationMode = false,
  onDrawComplete,
  onDeleteAnnotation,
  readOnly = false,
  t,
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [invert, setInvert] = useState(false);
  const [activePreset, setActivePreset] = useState('default');
  const [fullscreen, setFullscreen] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [viewingPrior, setViewingPrior] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showControls, setShowControls] = useState(false);

  const viewportRef = useRef(null);
  const wrapperRef = useRef(null);
  const panState = useRef(null);
  const drawState = useRef(null);

  const allThumbs = [
    ...images.map((img, i) => ({ ...img, isPrior: false, idx: i })),
    ...priorImages.map((img, i) => ({ ...img, isPrior: true, idx: i })),
  ];

  const currentImage = viewingPrior
    ? priorImages[currentIdx] || priorImages[0]
    : images[currentIdx] || images[0];

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setBrightness(100);
    setContrast(100);
    setInvert(false);
    setActivePreset('default');
  }, []);

  const applyPreset = useCallback((preset) => {
    setBrightness(preset.brightness);
    setContrast(preset.contrast);
    setInvert(false);
    setActivePreset(preset.id);
  }, []);

  // Non-passive wheel zoom
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e) => {
      e.preventDefault();
      setZoom((z) => Math.min(8, Math.max(0.5, z + (e.deltaY < 0 ? 0.15 : -0.15))));
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [fullscreen]);

  // Escape closes fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const getPercentCoords = (e) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    if (annotationMode && !viewingPrior && !readOnly) {
      const p = getPercentCoords(e);
      if (!p) return;
      drawState.current = p;
      setDraft({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      e.preventDefault();
      return;
    }
    panState.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (drawState.current) {
        const p = getPercentCoords(e);
        if (p) setDraft((d) => (d ? { ...d, x1: p.x, y1: p.y } : null));
        return;
      }
      if (panState.current) {
        setPan({
          x: panState.current.panX + (e.clientX - panState.current.startX),
          y: panState.current.panY + (e.clientY - panState.current.startY),
        });
      }
    };
    const onUp = () => {
      if (drawState.current) {
        drawState.current = null;
        setDraft((d) => {
          if (d) {
            const x = Math.min(d.x0, d.x1);
            const y = Math.min(d.y0, d.y1);
            const w = Math.abs(d.x1 - d.x0);
            const h = Math.abs(d.y1 - d.y0);
            if (w > 0.01 && h > 0.01 && onDrawComplete) {
              onDrawComplete({
                x_percent: x,
                y_percent: y,
                width_percent: w,
                height_percent: h,
                image_index: currentIdx,
              });
            }
          }
          return null;
        });
      }
      panState.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onDrawComplete, currentIdx]);

  const visibleMarkers = viewingPrior
    ? []
    : markers.filter((m) => (m.image_index || 0) === currentIdx);
  const visibleAnnotations = viewingPrior
    ? []
    : annotations.filter((a) => (a.image_index || 0) === currentIdx);

  const presentCategories = [...new Set([
    ...visibleMarkers.map((m) => m.category || 'BONE'),
    ...visibleAnnotations.map((a) => a.category || 'OTHER'),
  ])];

  const filterStyle = `brightness(${brightness}%) contrast(${contrast}%)${invert ? ' invert(1)' : ''}`;

  const renderBox = (m, i, isUser) => {
    const color = CATEGORY_COLORS[m.category || (isUser ? 'OTHER' : 'BONE')] || CATEGORY_COLORS.OTHER;
    return (
      <div
        key={isUser ? `ann-${m.id || i}` : `mk-${i}`}
        className="absolute pointer-events-none"
        style={{
          left: `${m.x_percent * 100}%`,
          top: `${m.y_percent * 100}%`,
          width: `${m.width_percent * 100}%`,
          height: `${m.height_percent * 100}%`,
          border: `3px ${isUser ? 'dashed' : 'solid'} ${color}`,
          borderRadius: '4px',
          boxShadow: `0 0 12px ${color}66, 0 0 4px ${color}aa`,
          zIndex: 10,
        }}
        data-testid={isUser ? `user-annotation-${i}` : `lesion-marker-${i}`}
      >
        <span
          className="absolute -top-6 left-0 text-black text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
          style={{ zIndex: 11, backgroundColor: color }}
        >
          {isUser ? '\u270E ' : ''}{m.label || `Lesion ${i + 1}`}
        </span>
        {isUser && !readOnly && onDeleteAnnotation && (
          <button
            className="absolute -top-2.5 -right-2.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center pointer-events-auto hover:bg-red-400"
            style={{ zIndex: 12 }}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteAnnotation(m.id);
            }}
            data-testid={`delete-annotation-${i}`}
          >
            <X size={11} />
          </button>
        )}
      </div>
    );
  };

  const viewerBody = (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-1 px-2 py-1.5 bg-[hsl(210,18%,7%)] border-b border-border flex-wrap">
        <div className="flex items-center gap-0.5">
          <ToolButton icon={ZoomIn} label={t('viewer.zoom_in')} onClick={() => setZoom((z) => Math.min(8, z + 0.25))} testId="viewer-zoom-in" />
          <ToolButton icon={ZoomOut} label={t('viewer.zoom_out')} onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} testId="viewer-zoom-out" />
          <ToolButton icon={SunMoon} label={t('viewer.invert')} onClick={() => setInvert((v) => !v)} active={invert} testId="viewer-invert" />
          <ToolButton icon={Sun} label={t('viewer.adjustments')} onClick={() => setShowControls((v) => !v)} active={showControls} testId="viewer-adjustments" />
          <ToolButton icon={RotateCcw} label={t('viewer.reset')} onClick={resetView} testId="viewer-reset" />
          <span className="text-[10px] text-muted-foreground ml-1 tabular-nums">{Math.round(zoom * 100)}%</span>
        </div>
        <div className="flex items-center gap-0.5">
          {annotationMode && (
            <span className="text-[10px] text-cyan-300 bg-cyan-500/10 px-2 py-0.5 rounded mr-1 flex items-center gap-1">
              <PenLine size={10} />
              {t('viewer.annotation_hint')}
            </span>
          )}
          <ToolButton
            icon={fullscreen ? Minimize2 : Maximize2}
            label={fullscreen ? t('viewer.exit_fullscreen') : t('viewer.fullscreen')}
            onClick={() => setFullscreen((f) => !f)}
            testId="viewer-fullscreen"
          />
        </div>
      </div>

      {/* Windowing presets bar */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[hsl(210,18%,7%)] border-b border-border overflow-x-auto" data-testid="window-presets-bar">
        <span className="text-[9px] text-muted-foreground uppercase tracking-wide shrink-0 mr-0.5">
          {t('viewer.windowing')}
        </span>
        {WINDOW_PRESETS.map((preset) => (
          <button
            key={preset.id}
            onClick={() => applyPreset(preset)}
            className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-semibold ring-1 transition-colors duration-150 ${
              activePreset === preset.id
                ? 'bg-cyan-500/20 text-cyan-300 ring-cyan-500/40'
                : 'bg-[hsl(210,14%,12%)] text-muted-foreground ring-[hsl(210,14%,20%)] hover:text-foreground hover:ring-[hsl(210,14%,30%)]'
            }`}
            data-testid={`preset-${preset.id}`}
          >
            {t(`viewer.preset_${preset.id}`)}
          </button>
        ))}
        {activePreset === null && (
          <span className="shrink-0 px-2 py-1 rounded-full text-[10px] text-teal-300 bg-teal-500/10 ring-1 ring-teal-500/25">
            {t('viewer.preset_custom')}
          </span>
        )}
      </div>

      {/* Brightness/contrast sliders */}
      {showControls && (
        <div className="px-4 py-2.5 bg-[hsl(210,18%,7%)] border-b border-border grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <Sun size={13} className="text-muted-foreground shrink-0" />
            <Slider
              value={[brightness]}
              min={40}
              max={200}
              step={5}
              onValueChange={(v) => {
                setBrightness(v[0]);
                setActivePreset(null);
              }}
              className="flex-1"
              data-testid="viewer-brightness-slider"
            />
            <span className="text-[10px] text-muted-foreground w-8 tabular-nums">{brightness}%</span>
          </div>
          <div className="flex items-center gap-2">
            <Contrast size={13} className="text-muted-foreground shrink-0" />
            <Slider
              value={[contrast]}
              min={40}
              max={250}
              step={5}
              onValueChange={(v) => {
                setContrast(v[0]);
                setActivePreset(null);
              }}
              className="flex-1"
              data-testid="viewer-contrast-slider"
            />
            <span className="text-[10px] text-muted-foreground w-8 tabular-nums">{contrast}%</span>
          </div>
        </div>
      )}

      {/* Viewport */}
      <div
        ref={viewportRef}
        className={`relative bg-black overflow-hidden flex items-center justify-center select-none ${
          annotationMode && !viewingPrior ? 'cursor-crosshair' : 'cursor-grab active:cursor-grabbing'
        } ${fullscreen ? 'flex-1' : ''}`}
        style={fullscreen ? {} : { height: '480px' }}
        onMouseDown={handleMouseDown}
        data-testid="image-viewer-viewport"
      >
        {currentImage && (
          <div
            ref={wrapperRef}
            className="relative inline-block"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: 'center center',
              transition: panState.current ? 'none' : 'transform 150ms ease-out',
            }}
          >
            <img
              src={`data:${currentImage.image_mime || 'image/jpeg'};base64,${currentImage.image_base64}`}
              alt="Medical"
              className="block pointer-events-none"
              style={{
                maxHeight: fullscreen ? '82vh' : '460px',
                maxWidth: '100%',
                filter: filterStyle,
              }}
              draggable={false}
              data-testid="analysis-image"
            />
            {visibleMarkers.map((m, i) => renderBox(m, i, false))}
            {visibleAnnotations.map((a, i) => renderBox(a, i, true))}
            {draft && (
              <div
                className="absolute pointer-events-none border-2 border-dashed border-cyan-300 bg-cyan-400/10"
                style={{
                  left: `${Math.min(draft.x0, draft.x1) * 100}%`,
                  top: `${Math.min(draft.y0, draft.y1) * 100}%`,
                  width: `${Math.abs(draft.x1 - draft.x0) * 100}%`,
                  height: `${Math.abs(draft.y1 - draft.y0) * 100}%`,
                  zIndex: 15,
                }}
              />
            )}
          </div>
        )}

        {/* Prior badge */}
        {viewingPrior && (
          <div className="absolute top-2 left-2 bg-purple-500/80 text-white text-[10px] font-bold px-2 py-1 rounded z-20">
            {t('viewer.prior_exam')}
          </div>
        )}

        {/* Legend */}
        {presentCategories.length > 0 && (
          <div className="absolute bottom-2 right-2 bg-black/75 backdrop-blur-sm px-3 py-2 rounded-md flex flex-col gap-1 z-20" data-testid="marker-legend">
            {presentCategories.map((cat) => (
              <div key={cat} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm border-2" style={{ borderColor: CATEGORY_COLORS[cat] || CATEGORY_COLORS.OTHER }} />
                <span className="text-[10px] font-medium" style={{ color: CATEGORY_COLORS[cat] || CATEGORY_COLORS.OTHER }}>
                  {t(`categories.${cat}`)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {allThumbs.length > 1 && (
        <div className="flex gap-2 px-2 py-2 bg-[hsl(210,18%,7%)] border-t border-border overflow-x-auto">
          {allThumbs.map((thumb, i) => {
            const active = thumb.isPrior === viewingPrior && thumb.idx === currentIdx;
            return (
              <button
                key={i}
                onClick={() => {
                  setViewingPrior(thumb.isPrior);
                  setCurrentIdx(thumb.idx);
                  setPan({ x: 0, y: 0 });
                  setZoom(1);
                }}
                className={`relative shrink-0 rounded-md overflow-hidden border-2 transition-colors duration-150 ${
                  active ? 'border-cyan-400' : 'border-transparent hover:border-[hsl(210,14%,30%)]'
                }`}
                data-testid={`thumbnail-${thumb.isPrior ? 'prior' : 'current'}-${thumb.idx}`}
              >
                <img
                  src={`data:${thumb.image_mime || 'image/jpeg'};base64,${thumb.image_base64}`}
                  alt={`Thumb ${i}`}
                  className="h-14 w-20 object-cover"
                />
                {thumb.isPrior && (
                  <span className="absolute bottom-0 left-0 right-0 bg-purple-500/90 text-white text-[8px] font-bold text-center py-0.5">
                    {t('viewer.prior_short')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[200] bg-black flex flex-col" data-testid="viewer-fullscreen-overlay">
        {viewerBody}
      </div>
    );
  }

  return <div className="flex flex-col">{viewerBody}</div>;
};

export default ImageViewer;
